import { ErrorHandler, NgZone, isDevMode } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { isAppRoute } from '../utils/public-route';

/// Sentry SDK'sı (~41 KB gzip, bkz. sentry-sdk.ts) ilk pakette olmasın diye tembel
/// yüklenir. Uygulama rotalarında ilk boyamadan sonra boşta; public (prerender)
/// sayfalarda `load` olayından PUBLIC_PAGE_DELAY_MS sonra (ya da bir uygulama
/// rotasına geçilince hemen): yavaş mobil ağda ilk ziyaretin baytlarıyla
/// (HTML, yazı tipleri, hidrasyon JS'i) yarışmasın. Hidrasyon/uygulama JS'i bundan
/// etkilenmez; yalnız izleme SDK'sı gecikir. Yüklenene
/// kadar yakalanan hatalar tamponlanır ve SDK hazır olunca gönderilir; SDK'nın
/// kendi global dinleyicileri init'ten önceki hataları kaçırabilir (kabul edildi).

// Public: Sentry DSN'leri tarayıcı paketine gömülmek üzere tasarlanmıştır.
// Boş/yer tutucu kalırsa SDK yüklenmez (isSentryDsnConfigured).
const DSN_PLACEHOLDER = 'REPLACE_WITH_ANGULAR_SENTRY_DSN';
const SENTRY_DSN: string = DSN_PLACEHOLDER;
const IDLE_TIMEOUT_MS = 3000;
export const PUBLIC_PAGE_DELAY_MS = 5000;
const MAX_BUFFERED_ERRORS = 20;

type SentryModule = typeof import('./sentry-sdk');
export interface SentryUser {
  id: string;
  username?: string;
  email?: string;
}

let sentry: SentryModule | undefined;
let pendingUser: SentryUser | null | undefined;
let delegate: ErrorHandler | undefined;
const buffered: unknown[] = [];

/// Oturum açan kullanıcıyı sonraki Sentry olaylarına bağlar (SDK yüklenmediyse yüklenince uygulanır).
export function setSentryUser(user: SentryUser | null): void {
  pendingUser = user;
  sentry?.setUser(user);
}

/// Angular ErrorHandler: SDK hazırsa Sentry'nin işleyicisine, değilse konsol + tampona.
export class LazySentryErrorHandler implements ErrorHandler {
  handleError(error: unknown): void {
    if (delegate) {
      delegate.handleError(error);
      return;
    }
    console.error(error);
    if (buffered.length < MAX_BUFFERED_ERRORS) buffered.push(error);
  }
}

function whenIdle(callback: () => void): void {
  const run = (): void => {
    setTimeout(callback, 0);
  };
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(run, { timeout: IDLE_TIMEOUT_MS });
  } else {
    setTimeout(callback, 1);
  }
}

type TracingSdk = Pick<SentryModule, 'addIntegration' | 'browserTracingIntegration' | 'TraceService'>;

/// Performans izlemeyi (en fazla bir kez) ekleyen fonksiyonu döndürür.
/// @sentry/angular 10'da browserTracingIntegration gezinti span'ı üretmez
/// (instrumentNavigation zorla kapalı); gezinti span'ları TraceService'ten gelir
/// (kurucusunda router olaylarına abone olur), bu yüzden onu da bir kez kurarız.
/// Sayfa public bir sayfada açıldıysa izleme ilk uygulama gezintisinde eklenir:
/// o durumda pageload span'ı public sayfa yüklemesinden gezintiye kadar uzamasın
/// diye instrumentPageLoad kapalı.
export function createTracingStarter(sdk: TracingSdk, router: Router, startedOnAppRoute: boolean): () => void {
  let added = false;
  return () => {
    if (added) return;
    added = true;
    sdk.addIntegration(sdk.browserTracingIntegration({ instrumentPageLoad: startedOnAppRoute }));
    new sdk.TraceService(router);
  };
}

async function load(router: Router, startedOnAppRoute: boolean, dsn: string): Promise<void> {
  const Sentry = await import('./sentry-sdk');
  Sentry.init({
    dsn,
    environment: isDevMode() ? 'debug' : 'production',
    tracesSampleRate: isDevMode() ? 1.0 : 0.2,
    // 401/403 (jeton yenileme, yetki reddi) ve çevrimdışı/ağ hataları beklenen gürültü.
    beforeSend(event, hint) {
      const err = hint?.originalException as { status?: number; message?: string } | undefined;
      if (err?.status === 401 || err?.status === 403) return null;
      if (err?.message && /Network|offline|Failed to fetch/i.test(err.message)) return null;
      return event;
    },
  });
  sentry = Sentry;
  if (pendingUser !== undefined) Sentry.setUser(pendingUser);
  delegate = Sentry.createErrorHandler();
  // Tampondakiler yakalandıklarında zaten konsola yazıldı; ikinci kez yazma.
  const replay = Sentry.createErrorHandler({ logErrors: false });
  for (const error of buffered.splice(0)) replay.handleError(error);

  // Performans izleme yalnız uygulama rotalarında, ilk uygulama gezintisinde eklenir.
  const addTracing = createTracingStarter(Sentry, router, startedOnAppRoute);
  if (isAppRoute(location.pathname)) addTracing();
  router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe((e) => {
    if (isAppRoute(e.urlAfterRedirects)) addTracing();
  });
}

/// SDK yüklemesinin ne zaman başlayacağını planlar (`start` en fazla bir kez çağrılır).
/// Uygulama rotası: hemen. Public sayfa: `load` + PUBLIC_PAGE_DELAY_MS ya da ilk
/// uygulama rotası gezintisi, hangisi önceyse.
export function scheduleSentryStart(
  onAppRoute: boolean,
  appNavigation$: Observable<unknown>,
  start: () => void,
  win: Window = window,
): void {
  let started = false;
  const once = (): void => {
    if (started) return;
    started = true;
    start();
  };
  if (onAppRoute) {
    once();
    return;
  }
  appNavigation$.pipe(take(1)).subscribe(once);
  const delayed = (): void => {
    win.setTimeout(once, PUBLIC_PAGE_DELAY_MS);
  };
  if (win.document.readyState === 'complete') delayed();
  else win.addEventListener('load', delayed, { once: true });
}

/// DSN boş ya da yer tutucuysa SDK hiç yüklenmez (~41 KB boşa inmesin, "Invalid Sentry Dsn" yok).
export function isSentryDsnConfigured(dsn: string): boolean {
  const value = dsn.trim();
  return value !== '' && value !== DSN_PLACEHOLDER;
}

/// Yalnız tarayıcıda çağrılır; SDK'yı boşta yükler (zamanlama: scheduleSentryStart).
/// DSN yapılandırılmamışsa hiçbir şey yapmaz; hata yakalayıcı yalnız konsola yazar.
/// Zamanlayıcı Angular bölgesi dışında: bekleyen gecikme uygulamayı "kararsız"
/// tutmasın (service worker registerWhenStable, hidrasyon sonrası kararlılık).
export function initSentryLazily(router: Router, zone: NgZone, dsn: string = SENTRY_DSN): void {
  if (!isSentryDsnConfigured(dsn)) return;
  const appNavigation$ = router.events.pipe(
    filter((e): e is NavigationEnd => e instanceof NavigationEnd && isAppRoute(e.urlAfterRedirects)),
  );
  const startedOnAppRoute = isAppRoute(location.pathname);
  zone.runOutsideAngular(() =>
    scheduleSentryStart(startedOnAppRoute, appNavigation$, () =>
      whenIdle(() => {
        load(router, startedOnAppRoute, dsn).catch((err: unknown) => console.error('Sentry yüklenemedi', err));
      }),
    ),
  );
}
